import { Component, OnDestroy, OnInit } from "@angular/core";
import { PageEvent } from "@angular/material/paginator";
import { IPriceResponse, ServiceRequestPaymentStatus } from "@gloria-public/order/domain/interfaces/cart.interface";
import { IOrdersHistory, IOrdersHistoryFilters, OrderStatus } from "@gloria-public/order/domain/interfaces/order.interface";
import { OrderService } from "@gloria-public/order/domain/services/order.service";
import { TranslateService } from "@ngx-translate/core";
import { BackendTableDataSource } from "@shared/utils/backend-table.datasource";
import { Subject, combineLatest, distinctUntilChanged, map, of, startWith, switchMap, takeUntil } from "rxjs";

export interface IOrdersHistoryView extends IOrdersHistory {
  totalPrice: IPriceResponse;
  collapsed: boolean;
}

function enrichOrders(orders: IOrdersHistory[]): IOrdersHistoryView[] {
  return orders.map((o) => ({
    ...o,
    collapsed: false,
    totalPrice: o.items.reduce(
      (acc, item) => ({
        value: acc.value + item.pricePerUnit.value,
        currency: item.pricePerUnit.currency,
      }),
      { value: 0, currency: "EUR" }
    ),
  }));
}

@Component({
  selector: "app-order-history-page",
  templateUrl: "./order-history-page.component.html",
  styleUrls: ["./order-history-page.component.scss"],
})
export class OrderHistoryPageComponent implements OnInit, OnDestroy {
  public ordersDataSource!: BackendTableDataSource<IOrdersHistoryView>;
  public totalCount = 0;
  public readonly pageSizeOptions = [5, 10, 20, 50];
  public ordersFilters: IOrdersHistoryFilters = {
    lang: this.translateService.currentLang,
    page: 1,
    pageSize: 10,
    sortByDate: "DESCENDING",
  };
  public readonly skeletonRows = Array.from({ length: 4 });
  public readonly OrderStatus = OrderStatus;
  public readonly ServiceRequestPaymentStatus = ServiceRequestPaymentStatus;

  /** Emits when the component is destroyed, used to complete all active subscriptions */
  private onDestroy$ = new Subject<void>();

  constructor(
    private orderService: OrderService,
    private translateService: TranslateService
  ) {
    this.ordersDataSource = new BackendTableDataSource(
      (p) =>
        combineLatest([
          of(p),
          this.translateService.onLangChange.pipe(
            startWith({ lang: this.translateService.currentLang }),
            distinctUntilChanged((a, b) => a.lang === b.lang),
            takeUntil(this.onDestroy$)
          ),
        ]).pipe(
          switchMap(([params, event]) =>
            this.orderService
              .getOrdersHistory({
                ...params,
                lang: event.lang,
              })
              .pipe(map(enrichOrders))
          ),
          takeUntil(this.onDestroy$)
        ),
      () => this.orderService.getOrdersHistoryCount()
    );
  }

  /**
   * loads the Datasource and subscribe to its count.
   */
  ngOnInit(): void {
    this.ordersDataSource.count$.pipe(takeUntil(this.onDestroy$)).subscribe((count) => (this.totalCount = count));
    this.loadDataSource();
  }

  /**
   * Cleans up subscriptions and subjects when the component is destroyed.
   */
  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  /**
   * Loads data using current filters
   */
  public onPage(event: PageEvent): void {
    this.ordersFilters = {
      ...this.ordersFilters,
      page: event.pageIndex + 1,
      pageSize: event.pageSize,
    };

    this.loadDataSource();
  }

  /**
   * Loads orders using current filters
   */
  private loadDataSource(): void {
    this.ordersDataSource.loadData({
      lang: this.ordersFilters.lang ?? "en",
      page: this.ordersFilters.page ?? 1,
      pageSize: this.ordersFilters.pageSize ?? 10,
      sortByDate: this.ordersFilters.sortByDate,
    });
  }

  /**
   * Toggles the collapse state of an order.
   * @param order the order to toggle.
   */
  public toggleOrder(order: IOrdersHistoryView): void {
    order.collapsed = !order.collapsed;
  }
}
